import React, { useEffect } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import getAction from '@behavior/activity/getAction';
import { useTitleDispatch } from '@components/TitleContextProvider';
import ErrorPage from '@src/pages/ErrorPage';
import ActionDetailView from '@components/Activity/ActionDetailView';

const actionDetailTheme = createTheme({
  typography: { fontFamily: 'inherit', fontSize: 13 },
  palette: {
    mode: 'light',
    primary: { main: '#111418' },
    divider: '#e6e8ec',
    background: { paper: '#ffffff', default: '#ffffff' },
    text: { primary: '#1f2328', secondary: '#5b6470' },
  },
  shape: { borderRadius: 6 },
  components: {
    MuiButton: {
      styleOverrides: { root: { textTransform: 'none', fontFamily: 'inherit' } },
    },
    MuiAvatar: {
      styleOverrides: { root: { fontFamily: 'inherit' } },
    },
  },
});

type Props = {
  id: number;
  showPersonalData?: boolean;
};

function ActionContainer({ id, showPersonalData }: Props) {
  const { t } = useTranslation();
  const dispatch = useTitleDispatch();
  const { action, isLoading } = getAction(parseInt(id.toString()));

  useEffect(() => {
    dispatch({ pageTitle: t('page.action.title') });
  }, [dispatch, t]);

  if (isLoading) {
    return (
      <ThemeProvider theme={actionDetailTheme}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            bgcolor: '#ffffff',
          }}
        >
          <CircularProgress size={26} thickness={3} sx={{ color: '#111418' }} />
        </Box>
      </ThemeProvider>
    );
  }

  if (!action) {
    return <ErrorPage />;
  }

  return (
    <ThemeProvider theme={actionDetailTheme}>
      <ActionDetailView action={action} showPersonalData={showPersonalData} />
    </ThemeProvider>
  );
}

ActionContainer.defaultProps = { showPersonalData: false };

const mapStateToProps = (state: any, ownProps: any) => ({
  id: ownProps.match.params.id,
  showPersonalData: state.dataPrivacy?.showPersonalData ?? false,
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, null)
)(ActionContainer) as React.FC<Props>;
